import { z } from "zod";

export const PriorityEnum = z.number().int().min(1).max(4);
export const StatusEnum = z.enum(["backlog", "todo", "doing", "done"]);
export type TaskStatus = z.infer<typeof StatusEnum>;
export const WorkItemTypeEnum = z.enum(["task", "story", "bug"]);
export type WorkItemType = z.infer<typeof WorkItemTypeEnum>;
export const SeverityEnum = z.enum(["trivial", "minor", "major", "critical"]);
export type Severity = z.infer<typeof SeverityEnum>;
export const ProjectKindEnum = z.enum(["standing", "initiative"]);
export const ProjectStatusEnum = z.enum(["active", "on_hold", "archived"]);
export type ProjectStatus = z.infer<typeof ProjectStatusEnum>;
export type ProjectKind = (typeof ProjectKindEnum.options)[number];

// Task row shape mirrors src/server/db/schema.ts tasks table.
// We hand-write rather than use drizzle-zod to keep shared/ free of server-only imports.
export const TaskRow = z.object({
  id: z.string(),
  projectId: z.string(),
  parentTaskId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: StatusEnum,
  priority: z.number().int(),
  type: WorkItemTypeEnum.default("task"),
  assigneeId: z.string().nullable(),
  dueDate: z.string().nullable(),
  startDate: z.string().nullable(),
  // Resource-levelling lock (stored 0/1 in D1; coerced to a boolean here).
  startLocked: z.coerce.boolean().default(false),
  startedAt: z.string().nullable().default(null),
  estimate: z.number().nullable(),
  milestoneId: z.string().nullable().default(null),
  epicId: z.string().nullable().default(null),
  severity: SeverityEnum.nullable().default(null),
  reproSteps: z.string().nullable().default(null),
  environment: z.string().nullable().default(null),
  sortOrder: z.number(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  completedAt: z.string().nullable(),
  assigneeIds: z.array(z.string()).default([]),
  watcherIds: z.array(z.string()).default([]),
  subtaskCount: z.number().int().default(0),
  subtaskCompletedCount: z.number().int().default(0),
  commentCount: z.number().int().default(0),
  attachmentCount: z.number().int().default(0),
  // True when at least one blocker task is not yet done. Injected by the
  // list/detail endpoints; absent rows default to not-blocked.
  isBlocked: z.boolean().default(false),
});

// A lightweight task reference used by the dependency lists (blocked-by /
// blocking) so the detail panel can render links without a full task fetch.
export const TaskDependencyRef = z.object({
  id: z.string(),
  title: z.string(),
  status: StatusEnum,
  type: WorkItemTypeEnum.default("task"),
});
export type TaskDependencyRef = z.infer<typeof TaskDependencyRef>;
export type TaskDependenciesResponse = {
  blockedBy: TaskDependencyRef[];
  blocks: TaskDependencyRef[];
};

export const CreateTaskInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(20000).optional(),
  status: StatusEnum.optional(),
  priority: PriorityEnum.optional(),
  type: WorkItemTypeEnum.optional(),
  // `.min(1)` rejects empty strings — without it `assigneeId: ""` falls
  // through every `if (assigneeId)` guard while still being a "present" value.
  assigneeId: z.string().min(1).optional(),
  dueDate: z.string().min(1).optional(),
  startDate: z.string().min(1).optional(),
  estimate: z.number().nonnegative().max(100000).optional(),
  milestoneId: z.string().min(1).nullable().optional(),
  epicId: z.string().min(1).nullable().optional(),
  severity: SeverityEnum.nullable().optional(),
  reproSteps: z.string().max(20000).nullable().optional(),
  environment: z.string().max(2000).nullable().optional(),
  parentTaskId: z.string().nullable().optional(),
});

export const UpdateTaskInput = CreateTaskInput.partial().extend({
  status: StatusEnum.optional(),
  type: WorkItemTypeEnum.optional(),
  prevId: z.string().nullable().optional(),
  nextId: z.string().nullable().optional(),
  // `null` is the unassign signal — `undefined` would be a no-op on PATCH.
  // CreateTaskInput's `assigneeId: z.string().min(1).optional()` rejects null,
  // so we override here. The `.min(1)` still rejects empty strings.
  assigneeId: z.string().min(1).nullable().optional(),
  dueDate: z.string().min(1).nullable().optional(),
  startDate: z.string().min(1).nullable().optional(),
  estimate: z.number().nonnegative().max(100000).nullable().optional(),
  milestoneId: z.string().min(1).nullable().optional(),
  epicId: z.string().min(1).nullable().optional(),
  severity: SeverityEnum.nullable().optional(),
  reproSteps: z.string().max(20000).nullable().optional(),
  environment: z.string().max(2000).nullable().optional(),
  description: z.string().max(20000).nullable().optional(),
  startLocked: z.boolean().optional(),
});

export const EpicRow = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  color: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  taskCount: z.number().int().default(0),
  doneCount: z.number().int().default(0),
});
export type EpicRow = z.infer<typeof EpicRow>;

export const CreateEpicInput = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(20000).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
});
export type CreateEpicInput = z.infer<typeof CreateEpicInput>;

export const UpdateEpicInput = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(20000).nullable().optional(),
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .nullable()
    .optional(),
});
export type UpdateEpicInput = z.infer<typeof UpdateEpicInput>;

export const MilestoneRow = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  dueDate: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Injected by the list endpoint: rollup of child task progress.
  taskCount: z.number().int().default(0),
  doneCount: z.number().int().default(0),
  // Sum of child task effort estimates (0 when none are set).
  estimateSum: z.number().default(0),
});
export type MilestoneRow = z.infer<typeof MilestoneRow>;

export const CreateMilestoneInput = z.object({
  name: z.string().min(1).max(120),
  dueDate: z.string().min(1).optional(),
});
export type CreateMilestoneInput = z.infer<typeof CreateMilestoneInput>;

export const UpdateMilestoneInput = z.object({
  name: z.string().min(1).max(120).optional(),
  dueDate: z.string().min(1).nullable().optional(),
});
export type UpdateMilestoneInput = z.infer<typeof UpdateMilestoneInput>;

export const ProjectMemberPreview = z.object({
  id: z.string(),
  name: z.string().nullable(),
  image: z.string().nullable(),
});
export type ProjectMemberPreview = z.infer<typeof ProjectMemberPreview>;

export const ProjectRow = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: ProjectStatusEnum,
  kind: ProjectKindEnum,
  targetDate: z.string().nullable(),
  ownerId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Task 3.9: display fields (nullable — existing rows have NULL)
  icon: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  category: z.string().nullable().optional(),
  healthOverride: z.enum(["green", "amber", "red"]).nullable().optional(),
  healthReason: z.string().nullable().optional(),
  // Computed counts injected by the list/recent endpoints
  completedCount: z.number().int(),
  totalCount: z.number().int(),
  // Project members (≤3, minimal shape for AvatarGroup)
  members: z.array(ProjectMemberPreview),
  // T46: project tags (full list, injected by list/detail endpoints)
  tags: z
    .array(z.object({ id: z.string(), name: z.string(), color: z.string() }))
    .optional(),
});

export const CreateProjectInput = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(20000).optional(),
  kind: ProjectKindEnum,
  targetDate: z.string().optional(),
  // Project accent color (hex). Optional — when omitted, the DB column
  // stays NULL and the UI falls back to a default swatch. The column is
  // already on the projects table (`text("color")`).
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
});

export const UpdateProjectInput = CreateProjectInput.partial().extend({
  status: ProjectStatusEnum.optional(),
  // Health override; pass null to clear back to auto-computed. A reason is
  // required (route-enforced) when setting a non-null override.
  healthOverride: z.enum(["green", "amber", "red"]).nullable().optional(),
  healthReason: z.string().max(500).nullable().optional(),
  // T46: full replacement set of tag names. Unknown names are auto-created
  // in the global tags table; omit the field to leave tags untouched.
  tagNames: z.array(z.string().min(1).max(40)).max(10).optional(),
});

// Reactions — a small fixed emoji set (raster, no custom uploads per scope).
export const REACTION_EMOJI = [
  "👍",
  "🎉",
  "❤️",
  "🚀",
  "👀",
  "✅",
  "😄",
  "🙏",
] as const;
export const ReactionEmojiEnum = z.enum(REACTION_EMOJI);
export const ReactionTargetEnum = z.enum(["task", "comment"]);

export const ReactionSummary = z.object({
  emoji: z.string(),
  count: z.number().int(),
  reactedByMe: z.boolean(),
  users: z.array(z.string()), // reactor display names, for the hover list
});
export type ReactionSummary = z.infer<typeof ReactionSummary>;
export type ReactionsResponse = { reactions: ReactionSummary[] };

export const ToggleReactionInput = z.object({ emoji: ReactionEmojiEnum });

// Teams
export const TeamMemberLite = z.object({
  userId: z.string(),
  name: z.string(),
  isLead: z.boolean().default(false),
});
export const TeamRow = z.object({
  id: z.string(),
  name: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  members: z.array(TeamMemberLite).default([]),
});
export type TeamRow = z.infer<typeof TeamRow>;
export const CreateTeamInput = z.object({ name: z.string().min(1).max(80) });
export const AddTeamMemberInput = z.object({
  userId: z.string().min(1),
  isLead: z.boolean().optional(),
});

// Announcements (workspace broadcasts)
export const AnnouncementRow = z.object({
  id: z.string(),
  title: z.string(),
  body: z.string(),
  pinned: z.boolean(),
  expiresAt: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  teamId: z.string().nullable().optional(), // null = workspace-wide
  teamName: z.string().nullable().optional(),
  // Per-request enrichments:
  dismissed: z.boolean().default(false), // has the current user dismissed it
  readCount: z.number().int().default(0), // total recipients who dismissed/read
});
export type AnnouncementRow = z.infer<typeof AnnouncementRow>;

export const CreateAnnouncementInput = z.object({
  title: z.string().min(1).max(200),
  body: z.string().max(10000).optional(),
  pinned: z.boolean().optional(),
  expiresAt: z.string().min(1).nullable().optional(),
  teamId: z.string().nullable().optional(),
});
export type CreateAnnouncementInput = z.infer<typeof CreateAnnouncementInput>;

// Documents (project knowledge base)
export const DocKindEnum = z.enum([
  "doc",
  "meeting",
  "decision",
  "note",
  "adr",
]);
export type DocKind = z.infer<typeof DocKindEnum>;

/** ADR lifecycle. Non-ADR docs carry a null status. */
export const AdrStatusEnum = z.enum(["proposed", "accepted", "superseded"]);
export type AdrStatus = z.infer<typeof AdrStatusEnum>;

// Dashboards ---------------------------------------------------------------
export const WidgetTypeEnum = z.enum([
  "count",
  "breakdown",
  "task_list",
  "throughput",
]);
export type WidgetType = z.infer<typeof WidgetTypeEnum>;

export const WidgetScopeEnum = z.enum(["workspace", "project", "team"]);

export const DashboardWidgetRow = z.object({
  id: z.string(),
  type: WidgetTypeEnum,
  scopeKind: WidgetScopeEnum,
  scopeId: z.string().nullable(),
  sortOrder: z.number(),
  config: z.record(z.string(), z.unknown()).nullable().optional(),
});
export type DashboardWidgetRow = z.infer<typeof DashboardWidgetRow>;

export const DashboardRow = z.object({
  id: z.string(),
  name: z.string(),
  ownerId: z.string(),
  shared: z.boolean(),
  mine: z.boolean().optional(),
});
export type DashboardRow = z.infer<typeof DashboardRow>;

export const CreateDashboardInput = z.object({
  name: z.string().min(1).max(120),
  shared: z.boolean().optional(),
});
export const CreateWidgetInput = z.object({
  type: WidgetTypeEnum,
  scopeKind: WidgetScopeEnum.optional(),
  scopeId: z.string().nullable().optional(),
  config: z.record(z.string(), z.unknown()).optional(),
});

// Wiki ---------------------------------------------------------------------
export const WikiPageRow = z.object({
  id: z.string(),
  projectId: z.string(),
  parentId: z.string().nullable(),
  slug: z.string(),
  title: z.string(),
  body: z.string(),
  updatedAt: z.string(),
});
export type WikiPageRow = z.infer<typeof WikiPageRow>;

export const CreateWikiPageInput = z.object({
  title: z.string().min(1).max(200),
  body: z.string().max(200000).optional(),
  parentId: z.string().nullable().optional(),
});
export type CreateWikiPageInput = z.infer<typeof CreateWikiPageInput>;

export const UpdateWikiPageInput = z.object({
  title: z.string().min(1).max(200).optional(),
  body: z.string().max(200000).optional(),
  parentId: z.string().nullable().optional(),
});
export type UpdateWikiPageInput = z.infer<typeof UpdateWikiPageInput>;

// Requirements management --------------------------------------------------
export const RequirementStatusEnum = z.enum([
  "proposed",
  "approved",
  "dropped",
]);
export type RequirementStatus = z.infer<typeof RequirementStatusEnum>;

/** Coverage rolls up from linked tasks: none / partial / done. */
export const RequirementCoverageEnum = z.enum(["none", "partial", "done"]);
export type RequirementCoverage = z.infer<typeof RequirementCoverageEnum>;

export const RequirementRow = z.object({
  id: z.string(),
  projectId: z.string(),
  reqNumber: z.number(),
  title: z.string(),
  description: z.string(),
  priority: z.number(),
  status: RequirementStatusEnum,
  taskCount: z.number(),
  doneCount: z.number(),
  coverage: RequirementCoverageEnum,
});
export type RequirementRow = z.infer<typeof RequirementRow>;

export const CreateRequirementInput = z.object({
  title: z.string().min(1).max(200),
  description: z.string().max(10000).optional(),
  priority: z.number().int().min(1).max(4).optional(),
});
export type CreateRequirementInput = z.infer<typeof CreateRequirementInput>;

export const UpdateRequirementInput = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(10000).optional(),
  priority: z.number().int().min(1).max(4).optional(),
  status: RequirementStatusEnum.optional(),
});
export type UpdateRequirementInput = z.infer<typeof UpdateRequirementInput>;

// Resource allocation ------------------------------------------------------
export const AllocationRow = z.object({
  id: z.string(),
  userId: z.string(),
  userName: z.string().nullable().optional(),
  projectId: z.string(),
  projectName: z.string().nullable().optional(),
  percent: z.number(),
  startDate: z.string(),
  endDate: z.string(),
});
export type AllocationRow = z.infer<typeof AllocationRow>;

export const CreateAllocationInput = z.object({
  userId: z.string().min(1),
  percent: z.number().int().min(1).max(100),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type CreateAllocationInput = z.infer<typeof CreateAllocationInput>;

// Custom fields ------------------------------------------------------------
export const CustomFieldTypeEnum = z.enum([
  "text",
  "number",
  "select",
  "multi_select",
  "date",
  "checkbox",
]);
export type CustomFieldType = z.infer<typeof CustomFieldTypeEnum>;

export const CustomFieldRow = z.object({
  id: z.string(),
  projectId: z.string(),
  name: z.string(),
  type: CustomFieldTypeEnum,
  options: z.array(z.string()).nullable().optional(),
  sortOrder: z.number(),
});
export type CustomFieldRow = z.infer<typeof CustomFieldRow>;

export const CreateCustomFieldInput = z.object({
  name: z.string().min(1).max(60),
  type: CustomFieldTypeEnum,
  options: z.array(z.string().min(1).max(60)).max(40).optional(),
});
export type CreateCustomFieldInput = z.infer<typeof CreateCustomFieldInput>;

// A field value is a JSON-encodable scalar or string[] (for multi_select).
export const CustomFieldValueInput = z.object({
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.string()),
    z.null(),
  ]),
});
export type CustomFieldValueInput = z.infer<typeof CustomFieldValueInput>;

export const DocumentRow = z.object({
  id: z.string(),
  projectId: z.string(),
  kind: DocKindEnum,
  title: z.string(),
  body: z.string(),
  status: AdrStatusEnum.nullable().optional(),
  supersedesId: z.string().nullable().optional(),
  adrNumber: z.number().nullable().optional(),
  taskId: z.string().nullable().optional(),
  createdBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DocumentRow = z.infer<typeof DocumentRow>;

export const CreateDocumentInput = z.object({
  kind: DocKindEnum.optional(),
  title: z.string().min(1).max(200),
  body: z.string().max(100000).optional(),
});
export type CreateDocumentInput = z.infer<typeof CreateDocumentInput>;

export const UpdateDocumentInput = z.object({
  title: z.string().min(1).max(200).optional(),
  body: z.string().max(100000).optional(),
  status: AdrStatusEnum.optional(),
  supersedesId: z.string().nullable().optional(),
  // Link/unlink the document to a task in the same project (null clears it).
  taskId: z.string().nullable().optional(),
});
export type UpdateDocumentInput = z.infer<typeof UpdateDocumentInput>;

export const CreateCommentInput = z.object({
  body: z.string().min(1).max(20000),
  // When set, the new comment is a reply nested under this parent comment.
  parentId: z.string().min(1).optional(),
});

export const CommentRow = z.object({
  id: z.string(),
  taskId: z.string(),
  authorId: z.string(),
  body: z.string(),
  parentId: z.string().nullable().default(null),
  createdAt: z.string(),
});

export const TagRow = z.object({
  id: z.string(),
  name: z.string(),
  color: z.string(),
});

export const AttachmentRow = z.object({
  id: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
});

export const FileRow = AttachmentRow.extend({
  project_id: z.string(),
  project_name: z.string(),
  // Related task (direct or via comment) + uploader display name — used by
  // the Files browser cards/rows.
  related_task_id: z.string().nullable().optional(),
  task_title: z.string().nullable().optional(),
  uploader_name: z.string().nullable().optional(),
  uploader_image: z.string().nullable().optional(),
});

// T49: rows from GET /api/projects/:id/files — project-scoped files have
// task_id NULL; task/comment attachments carry the task title for grouping.
export const ProjectFileRow = AttachmentRow.extend({
  task_id: z.string().nullable(),
  comment_id: z.string().nullable(),
  task_title: z.string().nullable(),
  uploader_name: z.string().nullable(),
});

export const TeamMemberRow = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  image: z.string().nullable().optional(),
  openTaskCount: z.number(),
  projects: z.array(
    z.object({ id: z.string(), name: z.string(), role: z.string() }),
  ),
  // Instance-level role surfaced from users.role.
  role: z.enum(["admin", "member"]),
});

export const ActivityRow = z.object({
  id: z.string(),
  project_id: z.string(),
  task_id: z.string().nullable(),
  actor_id: z.string(),
  action: z.string(),
  payload_json: z.string(),
  created_at: z.string(),
});

// Per-task activity feed item — camelCase mirror of the activity_log row.
// Distinct from the shaped `ActivityItem` (used by /api/projects/:projectId/activity
// + <ActivityFeed>) because this endpoint returns a flatter raw shape so the
// client can format inline without joining task / actor metadata.
export const TaskActivityItem = z.object({
  id: z.string(),
  projectId: z.string(),
  taskId: z.string().nullable(),
  actorId: z.string(),
  // Joined from users; null when the actor row is gone. actorName falls back
  // to actorId in the UI, actorImage drives the avatar photo.
  actorName: z.string().nullable().optional(),
  actorImage: z.string().nullable().optional(),
  action: z.string(),
  payloadJson: z.string(),
  createdAt: z.string(),
});
export type TaskActivityItem = z.infer<typeof TaskActivityItem>;
export type TaskActivityResponse = { items: TaskActivityItem[] };

// Full member row for the /api/projects/:projectId/members route. Distinct
// from `ProjectMemberPreview` (used in the list+recent enrichment) because
// this one surfaces the role + email needed by the project Members tab.
export type ProjectMemberFull = {
  userId: string;
  name: string | null;
  image: string | null;
  email: string;
  role: "owner" | "member";
};
export type ProjectMembersResponse = { members: ProjectMemberFull[] };

export const MetricsDashboardResponse = z.object({
  myOpen: z.number(),
  dueThisWeek: z.number(),
  completedMonth: z.number(),
  teamOverdue: z.number(),
  completedSpark: z.array(z.number()).length(7),
  // New sub-counts
  myOpenDueSoon: z.number(), // open tasks assigned to me with due_date within 3 days
  completedPrevMonth: z.number(), // completed in days [-60, -30)
  teamOverdueProjectCount: z.number(), // distinct projects represented in teamOverdue
});
export type MetricsDashboardResponseT = z.infer<
  typeof MetricsDashboardResponse
>;

export const UserDTO = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable().optional(),
  // Instance-level role; per-project roles live on project_members.
  // Optional for backwards-compat with cached responses pre-RBAC (0008).
  role: z.enum(["admin", "member"]).optional(),
  // Uploaded avatar URL (better-auth `image` field). Null/absent when the
  // user hasn't uploaded a photo; the Avatar component falls back to initials.
  image: z.string().nullable().optional(),
});

export type CreateTaskInput = z.infer<typeof CreateTaskInput>;
export type UpdateTaskInput = z.infer<typeof UpdateTaskInput>;
export type CreateProjectInput = z.infer<typeof CreateProjectInput>;
export type UpdateProjectInput = z.infer<typeof UpdateProjectInput>;
export type CreateCommentInput = z.infer<typeof CreateCommentInput>;
export type TaskRow = z.infer<typeof TaskRow>;
export type ProjectRow = z.infer<typeof ProjectRow>;
export type CommentRow = z.infer<typeof CommentRow>;
export type TagRow = z.infer<typeof TagRow>;
export type AttachmentRow = z.infer<typeof AttachmentRow>;
export type FileRow = z.infer<typeof FileRow>;
export type ProjectFileRow = z.infer<typeof ProjectFileRow>;
export type TeamMemberRow = z.infer<typeof TeamMemberRow>;
export type ActivityRow = z.infer<typeof ActivityRow>;
export type UserDTO = z.infer<typeof UserDTO>;
